import { Dialog } from 'primereact/dialog'
import React, { useRef, useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import SharedInputText from '../components/Shared/SharedInputText'
import SharedButton from '../components/Shared/SharedButton'
import { Toast } from 'primereact/toast'
import supergitHIS from '../api/supergitHIS'
import toast from 'react-hot-toast'
import validSupergitHIS from '../api/validSupergitHIS'

export default function GalleryUpdate(props) {
  const {
    visible,
    setVisible,
    invoiceData,
    setInvoiceData,
    fetchAgain,
    creditNote = false,
  } = props
  const { t } = useTranslation()
  const formikRef = useRef(null)
  const toastRef = useRef(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingRow, setEditingRow] = useState(null)

  const formatDate = dateString => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  const formatCurrency = amount => {
    if (amount === undefined || amount === null) return '0.00'
    return parseFloat(amount).toFixed(2)
  }

  const invoiceOrRevoke = creditNote
    ? {
      CustomerRevokeID: invoiceData?.invoice?.CustomerID,
      RevokeDate: new Date(),
    }
    : {}

  const initialValues = {
    CustomerName: invoiceData?.invoice?.CustomerName || '',
    CustomerID: invoiceData?.invoice?.CustomerID || '',
    ...invoiceOrRevoke,
    CustomerOrderID: invoiceData?.invoice?.CustomerOrderID || '',
    OrderDate: invoiceData?.invoice?.OrderDate || '',
    GalleryName: invoiceData?.invoice?.GalleryName || '',
    GalleryAddress: invoiceData?.invoice?.GalleryAddress || '',
    Phone:
      invoiceData?.invoice?.Mobile ||
      invoiceData?.invoice?.Phones ||
      invoiceData?.invoice?.Phone2 ||
      '',
    SalesPerson: invoiceData?.invoice?.SalesPerson || '',
    Cashier: invoiceData?.invoice?.Cashier || '',
    TotalPrice: invoiceData?.invoice?.TotalPrice || 0,
    PaidAmount: invoiceData?.invoice?.PaidAmount || 0,
    UnpaidAmount: invoiceData?.invoice?.UnpaidAmount || 0,
    Balance: invoiceData?.invoice?.Balance || 0,
    InvoiceId: invoiceData?.invoice?.InvoiceId || '',
    LocalSerial: invoiceData?.invoice?.LocalSerial || '',
    Paid: invoiceData?.invoice?.Paid || false,
    InsuranceCompanyName: invoiceData?.invoice?.InsuranceCompanyName || '',
    InsuranceCustomerName: invoiceData?.invoice?.InsuranceCustomerName || '',
    PolicyNo: invoiceData?.invoice?.PolicyNo || '',
    items: invoiceData?.details ? [...invoiceData.details] : [],
    reason: '',
  }

  const handleSubmit = async values => {
    const { items, ...rest } = values
    try {
      setIsSubmitting(true)
      const response = await validSupergitHIS.post(
        creditNote
          ? `invoices/customerRevokes/edit/${invoiceData?.invoice?.CustomerOrderID}`
          : `invoices/customerInvoices/edit/${invoiceData?.invoice?.CustomerOrderID}`,
        {
          details: [...items],
          invoice: {
            ...invoiceData?.invoice,
            ...rest,
          },
        }
      )
      setInvoiceData(null)
      setVisible(false)
      fetchAgain()
      toast.success(response?.data?.message)
    } catch (err) {
      console.error('Error updating invoice:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getRevokeInvoiceDetails = async id => {
    try {
      const response = await validSupergitHIS.get(`invoices/details/${id}`)
      if (response?.data) {
        setInvoiceData({
          invoice: response.data.invoice,
          details: response.data.details,
        })
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  return (
    <>
      <Dialog
        header={creditNote ? t('Credit Note') : t('Edit Invoice')}
        visible={visible}
        onHide={() => setVisible(false)}
        pt={{
          root: { className: 'w-full lg:w-10/12 mx-4 overflow-auto' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-3' },
          closeButton: {
            className: 'hover:!bg-black/20 !outline-none !shadow-none',
          },
          closeButtonIcon: { className: '!text-white' },
        }}
      >
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {formik => (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full mb-6">
                <SharedInputText
                  label={t('Customer Name')}
                  name="CustomerName"
                  value={formik.values.CustomerName}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />

                <SharedInputText
                  label={t('Order Date')}
                  name="OrderDate"
                  value={formatDate(formik.values.OrderDate)}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Gallery')}
                  name="GalleryName"
                  value={formik.values.GalleryName}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Gallery Address')}
                  name="GalleryAddress"
                  value={formik.values.GalleryAddress}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Phone')}
                  name="Phone"
                  value={formik.values.Phone}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Sales Person')}
                  name="SalesPerson"
                  value={formik.values.SalesPerson}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Cashier')}
                  name="Cashier"
                  value={formik.values.Cashier}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Total Price')}
                  name="TotalPrice"
                  value={`${formatCurrency(formik.values.TotalPrice)} SAR`}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Paid Amount')}
                  name="PaidAmount"
                  value={`${formatCurrency(formik.values.PaidAmount)} SAR`}
                  onChange={e => {
                    const value =
                      parseFloat(e.target.value.replace(' SAR', '')) || 0
                    formik.setFieldValue('PaidAmount', value)
                    formik.setFieldValue(
                      'UnpaidAmount',
                      formik.values.TotalPrice - value
                    )
                  }}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Unpaid Amount')}
                  name="UnpaidAmount"
                  value={`${formatCurrency(formik.values.UnpaidAmount)} SAR`}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Balance')}
                  name="Balance"
                  value={`${formatCurrency(formik.values.Balance)} SAR`}
                  readOnly
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Invoice ID')}
                  name="InvoiceId"
                  value={formik.values.InvoiceId}
                  readOnly={invoiceData?.invoice?.InvoiceId ? true : false}
                  className="!p-1.5"
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      getRevokeInvoiceDetails(formik.values.InvoiceId)
                    }
                  }}
                />
                <SharedInputText
                  label={t('Local Serial')}
                  name="LocalSerial"
                  value={formik.values.LocalSerial}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Insurance Company')}
                  name="InsuranceCompanyName"
                  value={formik.values.InsuranceCompanyName}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Insurance Customer')}
                  name="InsuranceCustomerName"
                  value={formik.values.InsuranceCustomerName}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Policy Number')}
                  name="PolicyNo"
                  value={formik.values.PolicyNo}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
                <SharedInputText
                  label={t('Reason')}
                  name="Reason"
                  value={formik.values.Reason}
                  onChange={formik.handleChange}
                  className="!p-1.5"
                />
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">
                  {t('Order Items')}
                </h3>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('Code')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('Description')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('Qty')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('Unit Price')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('Real Discount')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('Base Amount')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('Net Price')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formik.values.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <SharedInputText
                              name={`items[${index}].ProductCode`}
                              value={item?.ProductCode}
                              className="!p-1.5 !m-0 w-full"
                              onChange={e =>
                                formik.setFieldValue(
                                  `items[${index}].ProductCode`,
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <SharedInputText
                              name={`items[${index}].Description`}
                              value={
                                item?.Description ||
                                item?.PrintInvoiceDescription
                              }
                              className="!p-1.5 !m-0 w-full"
                              onChange={e =>
                                formik.setFieldValue(
                                  `items[${index}].Description`,
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={item?.Quantity}
                              name={`items[${index}].Quantity`}
                              mode="decimal"
                              min={1}
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                              onValueChange={e =>
                                formik.setFieldValue(
                                  `items[${index}].Quantity`,
                                  e.value || 0
                                )
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={item?.UnitPrice || item?.UPrice}
                              name={`items[${index}].UnitPrice`}
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                              onValueChange={e =>
                                formik.setFieldValue(
                                  `items[${index}].UnitPrice`,
                                  e.value || 0
                                )
                              }
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              value={item?.realDiscount}
                              name={`items[${index}].realDiscount`}
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                              onValueChange={e =>
                                formik.setFieldValue(
                                  `items[${index}].realDiscount`,
                                  e.value || 0
                                )
                              }
                            />
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              disabled
                              value={
                                item?.Quantity.toFixed(2) *
                                (item?.UnitPrice.toFixed(2) ||
                                  item?.Unitprice.toFixed(2))
                              }
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <InputNumber
                              disabled
                              value={(
                                item?.Quantity *
                                (item?.UnitPrice || item?.Unitprice) -
                                item?.realDiscount
                              )?.toFixed(2)}
                              className="w-full"
                              inputClassName="!p-1.5 !m-0 w-full"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex justify-end gap-6 mt-6">
                <SharedButton
                  icon="pi pi-times"
                  label={t('Cancel')}
                  className="cancel-btn"
                  onClick={() => {
                    setVisible(false)
                    setInvoiceData(null)
                    formik.resetForm()
                  }}
                />
                <SharedButton
                  icon="pi pi-check"
                  label={t('Save')}
                  className="submit-btn"
                  onClick={formik.handleSubmit}
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}
        </Formik>
      </Dialog>
    </>
  )
}
